/**
 * Manufacturing Profile Page - User Profile Display
 *
 * Features:
 * - Displays all user details (name, phone, email, role, etc.)
 * - Clean and organized layout with sections
 * - Avatar with user initial
 * - Role and permission information
 * - Account metadata
 * - Back button to navigate to dashboard
 *
 * @author SAUBHAGYA Development Team
 * @since Epic 6
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  X,
  User,
  Phone,
  Mail,
  Shield,
  Calendar,
  CheckCircle2,
  Users,
  AlertCircle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Manufacturing Profile Component
 */
export const ManufacturingProfile: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  /**
   * Format date string
   */
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  /**
   * Format date and time
   */
  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Main Content */}
      <div className="flex-1 overflow-hidden px-0 py-4 lg:py-6">
        <div className="w-full h-full flex flex-col gap-4">
          {/* Profile Header Card */}
          <Card className="p-6 lg:p-8 bg-white rounded-lg shadow-sm border border-gray-200 flex-shrink-0 mx-4 sm:mx-6 lg:mx-8 relative">
            {/* Close Button - Top Right Corner */}
            <button
              onClick={() => navigate(-1)}
              className="absolute top-4 lg:top-6 right-4 lg:right-6 text-gray-400 hover:text-gray-700 hover:bg-gray-50 p-2 h-9 w-9 rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
              title="Close profile"
              aria-label="Close profile"
              tabIndex={0}
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex flex-col sm:flex-row items-center gap-6">
              {/* Profile Photo */}
              <div className="flex-shrink-0">
                <div className="w-28 h-28 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center border-4 border-blue-600 shadow-lg">
                  <span className="text-5xl font-bold text-white">
                    {user?.name ? user.name.charAt(0).toUpperCase() : 'M'}
                  </span>
                </div>
              </div>

              {/* User Info */}
              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">
                  {user?.name || 'Manufacturing User'}
                </h2>
                <p className="text-gray-500 text-sm font-medium">
                  {user?.role || 'Manufacturing Operator'}
                </p>
              </div>
            </div>
          </Card>

          {/* Main Content Card */}
          <Card className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1 flex flex-col overflow-hidden mx-4 sm:mx-6 lg:mx-8">
            <div className="p-6 lg:p-8 overflow-y-auto flex-1 h-full">
              <div className="space-y-8">
                {/* Section 1: Core Personal Information */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Personal Information
                    </h3>
                    <p className="text-sm text-gray-600">
                      Your account and contact details
                    </p>
                  </div>
                  <Separator />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Full Name */}
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-center gap-3">
                        <User className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Full Name
                        </label>
                      </div>
                      <p className="text-base font-medium text-gray-900">
                        {user?.name || '—'}
                      </p>
                      <Separator className="mt-2" />
                    </div>

                    {/* Phone Number */}
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Phone Number
                        </label>
                      </div>
                      <p className="text-base font-medium text-gray-900">
                        {user?.phone || '—'}
                      </p>
                      <Separator className="mt-2" />
                    </div>

                    {/* Email Address */}
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Email Address
                        </label>
                      </div>
                      <p className="text-base font-medium text-gray-900">
                        {user?.email || '—'}
                      </p>
                      <Separator className="mt-2" />
                    </div>

                    {/* User ID / External ID */}
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-center gap-3">
                        <Shield className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          User ID
                        </label>
                      </div>
                      <p className="text-base font-medium text-gray-900 font-mono text-sm">
                        {user?.id || user?.externalId || '—'}
                      </p>
                      <Separator className="mt-2" />
                    </div>
                  </div>
                </div>

                {/* Section 2: Role & Account Information */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Role & Access Information
                    </h3>
                    <p className="text-sm text-gray-600">
                      Your assigned roles and permissions
                    </p>
                  </div>
                  <Separator />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Primary Role */}
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-center gap-3">
                        <Shield className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Primary Role
                        </label>
                      </div>
                      <Badge className="bg-indigo-600 text-white px-2.5 py-0.5 text-xs w-fit">
                        {user?.role || 'Manufacturing Operator'}
                      </Badge>
                      <Separator className="mt-2" />
                    </div>

                    {/* App Type / Platform */}
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-center gap-3">
                        <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Platform
                        </label>
                      </div>
                      <p className="text-base font-medium text-gray-900 capitalize">
                        {user?.appType ? user.appType.replace(/([A-Z])/g, ' $1').trim() : 'Manufacturing'}
                      </p>
                      <Separator className="mt-2" />
                    </div>

                    {/* Account Status */}
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Account Status
                        </label>
                      </div>
                      <Badge className="bg-green-600 text-white px-2.5 py-0.5 text-xs w-fit">
                        ✓ Active
                      </Badge>
                      <Separator className="mt-2" />
                    </div>

                    {/* KYC Status */}
                    {user?.kycStatus && (
                      <div className="flex flex-col space-y-3">
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            KYC Status
                          </label>
                        </div>
                        <Badge
                          variant={
                            user.kycStatus === 'Verified'
                              ? 'default'
                              : user.kycStatus === 'Pending'
                                ? 'secondary'
                                : 'outline'
                          }
                          className="w-fit"
                        >
                          {user.kycStatus}
                        </Badge>
                        <Separator className="mt-2" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Section 3: All Assigned Roles */}
                {user?.roles && user.roles.length > 0 && (
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <Users className="w-5 h-5 mr-2 text-blue-600" />
                      All Assigned Roles ({user.roles.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {user.roles.map((role, idx) => (
                        <Badge
                          key={idx}
                          className="bg-blue-100 text-blue-800 border border-blue-300 px-3 py-1 text-xs font-medium"
                        >
                          {role}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Section 4: Permissions */}
                {user?.permissions && user.permissions.length > 0 && (
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <Shield className="w-5 h-5 mr-2 text-blue-600" />
                      Permissions ({user.permissions.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {user.permissions.map((permission, idx) => {
                        // Handle both string and object permissions
                        const permissionName =
                          typeof permission === 'string'
                            ? permission
                            : (permission as any)?.module || JSON.stringify(permission).substring(0, 30);

                        return (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="border border-gray-300 bg-white text-gray-800 px-3 py-1 text-xs font-medium hover:bg-gray-50"
                          >
                            {permissionName}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Section 5: Account Metadata */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                    Account Information
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <p className="text-xs font-medium text-gray-600 mb-2">
                        Account Created
                      </p>
                      <p className="text-sm text-gray-900 font-semibold">
                        {user?.lastAccess
                          ? formatDate(new Date().toISOString())
                          : 'Recent'}
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <p className="text-xs font-medium text-gray-600 mb-2">
                        Department
                      </p>
                      <p className="text-sm text-gray-900 font-semibold">
                        {user?.department || 'Manufacturing Unit'}
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <p className="text-xs font-medium text-gray-600 mb-2">
                        Locale / Language
                      </p>
                      <p className="text-sm text-gray-900 font-semibold capitalize">
                        {user?.locale || 'English'}
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <p className="text-xs font-medium text-gray-600 mb-2">
                        Access Level
                      </p>
                      <p className="text-sm text-gray-900 font-semibold capitalize">
                        {user?.role || 'Operator'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ManufacturingProfile;
